import React, { useMemo, useState } from 'react';
import EventList from '../../../components/facial/EventList';
import { facialService } from '../../../services/central-api/facial';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { FacialParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FacialSearchFilter from './Filters';
import Header from '../../../components/Header';
import { translate } from '../../../services/translate';
import Container from '../../../components/Container';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import ReportCountModal from './ReportCountModal';
import CustomButton from '../../../components/CustomButton';
import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import DataTableContainer from '../../../components/DataTableContainer';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCurrentApp } from '../../../context/CurrentApp';

interface SearchParams {
    navigation: Navigation;
}

const limit = 25;

export default function Search({ navigation }: SearchParams) {
    const { isParentApp } = useCurrentApp();
    const route = useRoute<RouteProp<FacialParamList, 'Search'>>();
    const [filters, setFilters] = useFilters<FacialParamList['Search']>({
        ...route.params,
    });
    const [isParentModalVisible, setParentModalVisible] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const { data, hasNextPage, fetchNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery({
        queryKey: ['searchFacialDetections', filters],
        enabled: isFocused,
        queryFn: ({ pageParam = 0 }) => {
            return facialService.getDetections({
                ...filters,
                limit,
                page: pageParam,
            });
        },
        getNextPageParam: (lastPage, allPages) =>
            lastPage.length < limit ? undefined : allPages.length,
    });

    const detections = useMemo(
        () => data?.pages.flat() ?? [],
        [data]
    );

    return (
        <Container>
            <Header
                title={translate('searchFacial')}
                actions={<>
                    {isParentApp && <CustomButton
                        type='secondary'
                        icon={faDownload as IconDefinition}
                        text={translate('detectionsCountReport')}
                        onPress={() => setParentModalVisible(true)}
                    />}
                </>}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredDetections')}
                    zIndex={2}
                    filter={<FacialSearchFilter filters={filters} setFilters={setFilters} />}
                />
                <DataTableContainer
                    dataTable={<EventList
                        events={detections}
                        navigation={navigation}
                        loadMore={() => {
                            if (!hasNextPage) {
                                return;
                            }
                            if (isFetchingNextPage) {
                                return;
                            }
                            fetchNextPage();
                        }}
                        isLoading={isLoading}
                        module='facial'
                        page='search'
                    />}
                />
            </Card>
            <ReportCountModal
                isModalVisible={isParentModalVisible}
                setModalVisible={setParentModalVisible}
            />
        </Container>
    );
}
